import React from "react";
import { Box, IconButton, Tab, Tabs, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { AppFilterSettings } from "../../models/app/AppGridModels";
import AppConfirmDialog from "./AppConfirmDialog";

export interface ManageShortcutsProps {
  savedFilters?: AppFilterSettings[];
  onApplySavedFilter?: (filter: AppFilterSettings) => void;
  onRemoveShortcut?: (filterName: string) => void;
  resetSignal?: number;
}

interface SavedFilterShortcutParams {
  getSavedFilters: () => AppFilterSettings[];
  persistFilterSettings: (filters: AppFilterSettings[]) => void;
  setSnackbar: (value: {
    open: boolean;
    message: string | React.ReactNode;
    severity: "success" | "error" | "warning" | "info";
  }) => void;
}

export const useSavedFilterShortcutHandlers = ({
  getSavedFilters,
  persistFilterSettings,
  setSnackbar,
}: SavedFilterShortcutParams) => {
  const handleRemoveShortcut = (filterName: string) => {
    try {
      const updatedFilters = getSavedFilters().map((filter) =>
        filter.filterShortcutName === filterName
          ? { ...filter, isShortcut: false }
          : filter,
      );

      persistFilterSettings(updatedFilters);

      setSnackbar({
        open: true,
        message: <>Shortcut removed for <b>{filterName}</b>.</>,
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to remove shortcut. Please try again.",
        severity: "error",
      });
    }
  };

  return {
    handleRemoveShortcut,
  };
};

const ManageShortcuts: React.FC<ManageShortcutsProps> = ({
  savedFilters,
  onApplySavedFilter,
  onRemoveShortcut,
  resetSignal,
}) => {
  const getFilterName = (filter: AppFilterSettings) =>
    ((filter.filterShortcutName && filter.filterShortcutName !== "null" && String(filter.filterShortcutName).trim()) || (filter as any).filterName || "").trim();

  const isShortcutEnabled = (filter: AppFilterSettings) =>
    Boolean(filter.isShortcut ?? (filter as any).isShortCut);

  const shortcutFilters = React.useMemo(
    () =>
      (savedFilters || []).filter(
        (filter) => isShortcutEnabled(filter) && getFilterName(filter) !== "",
      ),
    [savedFilters],
  );
  const [activeShortcut, setActiveShortcut] = React.useState<string | false>(
    false,
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [selectedShortcut, setSelectedShortcut] = React.useState("");

  React.useEffect(() => {
    if (!shortcutFilters.length) {
      setActiveShortcut(false);
      return;
    }

    const hasActive = shortcutFilters.some(
      (filter) => getFilterName(filter) === activeShortcut,
    );

    if (!hasActive) {
      setActiveShortcut(false);
    }
  }, [shortcutFilters, activeShortcut]);

  React.useEffect(() => {
    setActiveShortcut(typeof resetSignal === "string" ? resetSignal : false);
  }, [resetSignal]);

  const handleApplyShortcut = (value: string) => {
    const selectedFilter = shortcutFilters.find(
      (filter) => getFilterName(filter) === value,
    );

    if (selectedFilter) {
      setActiveShortcut(value);
      onApplySavedFilter?.(selectedFilter);
    }
  };

  const handleDeleteShortcutClick = (filterName: string) => {
    setSelectedShortcut(filterName);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDeleteShortcut = () => {
    onRemoveShortcut?.(selectedShortcut);
    setDeleteDialogOpen(false);
    setSelectedShortcut("");
  };

  if (!shortcutFilters.length) {
    return <Box sx={{ flex: 1 }} />;
  }

  return (
    <Box flex={1} minWidth={0} px={1}>
      <Tabs
        value={activeShortcut}
        onChange={(_, value) => handleApplyShortcut(value)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          minHeight: 36,
          "& .MuiTabs-flexContainer": {
            justifyContent: "center",
            alignItems: "center",
          },
          "&:has(.MuiTabs-scrollButtons:not(.Mui-disabled)) .MuiTabs-flexContainer": { justifyContent: "flex-start" },
          "& .MuiTabs-scroller": { px: 1 },
          "& .MuiTabs-scrollButtons": {
            width: 28,
          },
          "& .MuiTab-root": {
            minHeight: 32,
            textTransform: "none",
            borderRadius: 2,
            border: 1,
            borderColor: "divider",
            mr: 1,
            px: 1,
            py: 0,
          },
          "& .Mui-selected": {
            borderColor: "primary.main",
            backgroundColor: "action.selected",
          },
          "& .MuiTabs-indicator": {
            display: "none",
          },
        }}
      >
        {shortcutFilters.map((filter, index) => {
          const filterName = getFilterName(filter);
          const description = (
            filter.description || (filter as any).filterDescription || ""
          ).trim();

          return (
            <Tab
              key={`shortcut-${filterName || index}`}
              value={filterName}
              disableRipple
              label={
                <Tooltip title={description || ""} placement="top" arrow>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    {(filter as any).imageUri ? <Box component="img" src={(filter as any).imageUri} alt={filterName} sx={{ width: 24, height: 24, borderRadius: 0.75, objectFit: "cover" }} /> : <Box component="span">{filterName}</Box>}
                    {filterName && (
                      <IconButton
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleDeleteShortcutClick(filterName);
                        }}
                        sx={{
                          p: "2px",
                          borderRadius: "50%",
                          color: "text.secondary",
                          "&:hover": {
                            color: "common.white",
                            bgcolor: "error.main",
                          },
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </Tooltip>
              }
            />
          );
        })}
      </Tabs>
      <AppConfirmDialog
        isOpen={deleteDialogOpen}
        handleClose={() => setDeleteDialogOpen(false)}
        onFail={() => setDeleteDialogOpen(false)}
        onSuccess={handleConfirmDeleteShortcut}
        headerText="Delete Shortcut"
        bodyText={<>Are you sure you want to delete shortcut <b>{selectedShortcut}</b>?</>}
      />
    </Box>
  );
};

export default ManageShortcuts;
